import type { PageCategoryType } from '#types/gtm'
import type { OptimizelyAttributesInput } from '#types/p13n'
import ds from '#root/design-system'

const patterns = [
  { pattern: /\/p\//, value: 'PDP' },
  { pattern: /\/c\//, value: 'PLP' },
  { pattern: /\/cart/, value: 'Cart' },
  { pattern: /\/account/, value: 'My Account' },
  { pattern: /\/search/, value: 'Search' },
  { pattern: /\/agent-results/, value: 'Search' },
  { pattern: /searchhub/, value: 'SearchHub' },
  { pattern: /^\/[a-z]{2}-[a-z]{2}$/, value: 'Home' },
  { pattern: /\/order-confirmation/, value: 'Order Confirmation' },
  { pattern: /\/checkout/, value: 'Checkout' }
].map(({ pattern, value }) => ({
  pattern: new RegExp(pattern),
  value
}))

const getPagePattern = (path: string) => {
  for (const { pattern, value } of patterns) {
    if (pattern.test(path))
      return <PageCategoryType>value
  }

  return 'Other'
}

/**
 * Normalizes the `disableOptimizely` query param.
 * @param value - Raw query value (`string`, `string[]`, or missing).
 * @category Utils
 * @returns `'web' | 'fx' | 'all'` when something should be disabled; otherwise `null`
 *   (`false`, missing, or unrecognized values leave both Web and FX enabled).
 */
export const getDisableOptimizelyMode = (value: unknown): 'web' | 'fx' | 'all' | null => {
  const raw = Array.isArray(value) ? value[0] : value
  if (raw === 'web') return 'web'
  if (raw === 'fx') return 'fx'
  if (raw === 'true') return 'all'
  return null
}

/**
 * Whether Optimizely Web (snippet load + `window.optimizely` queue) should stay disabled.
 * Disabled for `disableOptimizely=web` or `disableOptimizely=true`.
 * @param value - Raw `disableOptimizely` query param.
 * @category Utils
 */
export const isOptimizelyWebDisabled = (value: unknown): boolean => {
  const mode = getDisableOptimizelyMode(value)
  return mode === 'web' || mode === 'all'
}

/**
 * Whether Optimizely Feature Experimentation (FX SDK decide/track) should stay disabled.
 * Disabled for `disableOptimizely=fx` or `disableOptimizely=true`.
 * @param value - Raw `disableOptimizely` query param.
 * @category Utils
 */
export const isOptimizelyFxDisabled = (value: unknown): boolean => {
  const mode = getDisableOptimizelyMode(value)
  return mode === 'fx' || mode === 'all'
}

/**
 * Extracts the page type from a given path using a set of predefined patterns.
 * @param path The path from which to extract the page type.
 * @category Utils
 * @returns The extracted page type, or 'Other' if no match is found.
 */
export const extractPageType = (path: string) => {
  if (document.querySelector('meta[name="description"]')?.getAttribute('content')?.includes('Page is not available'))
    return 'Error Page'

  return getPagePattern(path)
}

/**
 * Extracts the product ID encoded at the end of a PDP path (the trailing `-XX######` segment) and
 * concatenates the `color` query parameter when provided.
 * @param path - The PDP route path.
 * @param query - Route query.
 * @param query.color - Variant colour code to append to the extracted product id.
 * @returns The full product+color ID string, or `undefined` when the path doesn't match a PDP shape.
 * @category Utils
 */
export const getProductIdFromPath = (path: string, query?: { color?: string }): string | undefined => {
  const id = path.match(/-([A-Z]{2}[A-Z\d]{6,})$/)?.[1]
  return id && `${id}${query?.color || ''}`
}

/**
 * Extracts the trailing 4–7 digit category ID from a PLP path (before any query string).
 * @category Utils
 */
export const getCategoryIdFromPLPPath = (path: string) => path.match(/(\d{4,7})(?=\?|$)/)?.[1]

/**
 * Builds the BloomReach recommendations endpoint URL by suffixing `url` with the endpoint ID
 * registered for the current page type, falling back to the `Other` endpoint.
 * @category Utils
 */
export const getBloomReachUrl = (path: string, url: string, ids: Record<PageCategoryType, string>) =>
  `${url}${ids[extractPageType(path)] || ids.Other}`

/**
 * Builds the BloomReach query parameters for the current page. Determines the page type from the
 * path, populates `itemIds` from cart contents (Cart) or the path-derived product ID (PDP), and
 * applies special-case sizing/labels for SearchHub and Error pages.
 * @param siteId - BloomReach site identifier.
 * @param path - Current route path.
 * @param ctx - Recommendation context.
 * @param ctx.cart - The current cart (used to populate `itemIds` on the Cart page type).
 * @param ctx.query - Route query (used to extract the colour code on PDP).
 * @param ctx.endpointIds - Map of page type → BloomReach endpoint id.
 * @returns Parameter object ready to send to the BloomReach recommendations endpoint.
 * @category Utils
 */
export const getBloomReachParams = (siteId: string, path: string, { cart, query, endpointIds }) => {
  const itemIds = {}
  const extractedPageType = extractPageType(path)
  const pageType = extractedPageType === 'Error Page' ? 'Error Page' : (endpointIds[extractedPageType] ? extractedPageType : 'Other')

  if (pageType === 'Cart') {
    cart.items?.forEach((item, index) => {
      const pid = item.productTypeId === 'styleId'
        ? item.masterId2
        : item.masterId
      return itemIds[pid] = index + 1
    })
  }

  if (pageType === 'PDP') {
    const productId = getProductIdFromPath(path, query)
    itemIds[productId!] = 1
  }

  return {
    siteId,
    language: location.pathname.split('/')[1].split('-')[0],
    size: 16,
    itemIds: itemIds || null,
    ...(pageType === 'PLP' && { categoryIds: getCategoryIdFromPLPPath(path) }),
    ...(pageType === 'Other' && { pagePath: path }),
    ...(pageType === 'Error Page' && { pagePath: 'pageNotFound' })
  }
}

/**
 * Normalises a raw BloomReach (or BloomReach-search) product payload into the shape consumed by
 * Canvas product cards / carousels. Falls back across the various field names BloomReach uses
 * across recommendations and search responses.
 * @param product - Raw BloomReach product payload.
 * @param showRatings - When true, attaches the `rating` block (score + review count).
 * @returns Canvas-friendly product object with normalised id/image/price/rating fields.
 * @category Utils
 */
export const extractBloomReachProduct = (product, showRatings) => {
  const {
    highCurrent,
    highOriginal,
    historicBestPrice,
    id,
    image,
    images, // added to allow search products
    lowCurrent,
    lowOriginal,
    percentagePriceDiscount,
    previousBestSalesPrice,
    price, // added to allow search products
    productId,
    recommendationId,
    recommendationName,
    recommendationVariant,
    review_average,
    reviews_count,
    title,
    url,
  } = product
  const name = product.name // added to allow search products
  return {
    id: productId || id,
    image: {
      src: image || images, // fallback for search products
      alt: title,
      type: 'image'
    },
    price: {
      highCurrent: Number(highCurrent || price.highCurrent), // fallback for searchhub products
      lowCurrent: Number(lowCurrent || price.lowCurrent),
      lowOriginal: Number(lowOriginal || price.lowOriginal),
      highOriginal: Number(highOriginal || price.highOriginal),
      historicBestPrice: Number(historicBestPrice || price?.historicBestPrice),
      percentagePriceDiscount: Number(percentagePriceDiscount || price?.percentagePriceDiscount),
      previousBestSalesPrice: Number(previousBestSalesPrice || price?.previousBestSalesPrice)
    },
    recommendationId,
    recommendationName,
    recommendationVariant,
    title: title || name,
    name: title || name,
    url: url.replace(/^(?:https?:\/\/)?[^/]+/, ''),
    ...(showRatings && {
      rating: {
        showScore: review_average !== undefined,
        score: Number.parseFloat(review_average || '0'),
        ...reviews_count && { reviewsCount: reviews_count }
      }
    }),
  }
}

const concatenateUserStatuses = (userStore) => {
  const { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile } = userStore
  const userStatuses = { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile }
  return Object.entries(userStatuses)
    .filter(([_, v]) => v)
    .map(([k]) => k.replace(/^is/, '').replace(/(Member|Profile)$/, ''))
    .join('|')
}

/**
 * Returns the values of `attribute` for every cart item whose `productId` matches one in the
 * `products` list. Used by analytics builders to project cart subsets onto a single attribute.
 * @category Utils
 */
export const extractCartProducts = (cart, products, attribute: string) => cart.items.filter(
  (item) => products.map(({ productId }) => productId).includes(item.productId)
).map((p) => p[attribute])

/**
 * Builds the Optimizely attributes object for the current page/user/cart context. Combines the
 * page-type, cart totals, auth state, geo data, viewport size, and PDP/PLP-specific extras into
 * the flat attribute map Optimizely expects for targeting/segmentation.
 * @param input - All inputs required to derive the attribute map (`OptimizelyAttributesInput`).
 *                Fields: `auth`, `cart`, `cmabAttributes`, `customCookieAttributes`,
 *                `landingPage`, `locationData`, `productData`, `route`, `userStore`,
 *                `visitNumber`, `windowSize`.
 * @returns Flat attribute map ready to pass to Optimizely.
 * @category Utils
 */
export const getOptimizelyAttributes = (input: OptimizelyAttributesInput) => {
  const {
    auth,
    cart,
    cmabAttributes,
    customCookieAttributes,
    landingPage,
    locationData,
    productData,
    route,
    userStore,
    visitNumber,
    windowSize,
  } = input
  const pageType = getPagePattern(route.path)

  return {
    cartTotalAmount: cart?.totals?.total ?? 0,
    cartTotalItems: cart?.totalItems ?? 0,
    landingPage: landingPage || false,
    loggedIn: auth?.loggedIn || false,
    pageType,
    screenSizeHeight: windowSize.height,
    screenSizeWidth: windowSize.width,
    url: route.fullPath,
    visitNumber: Number(visitNumber || 0),
    ...(cart.items?.length > 0 && { cartItemsMasterId: cart.items.map((item) => item.masterId).join('|') }),
    ...(cart.items?.length > 0 && { cartItemsProductId: cart.items.map((item) => item.productId).join('|') }),
    ...(cart.totals?.discount > 0 && { cartTotalDiscount: cart.totals.discount }),
    ...(pageType === 'PDP' && { productId: getProductIdFromPath(route.path, route.query) }),
    ...(pageType === 'PDP' && { onSale: !!productData?.onSale }),
    ...(pageType === 'PDP' && { isCustoms: !!productData?.isCustoms }),
    ...(pageType === 'PLP' && { categoryIds: getCategoryIdFromPLPPath(route.path) }),
    ...(pageType === 'Search' && { searchQuery: route.query.q }),
    ...(auth?.loggedIn && { userInterests: userStore.interests.map((interest) => interest.name).join('|') }),
    ...(auth?.loggedIn && { userSegment: concatenateUserStatuses(userStore) }),
    ...(locationData?.latLong && { latitude: Number(locationData.latLong.split(',')[0]), longitude: Number(locationData.latLong.split(',')[1]) }),
    ...(locationData?.country && { country: locationData.country }),
    ...(locationData?.state && { state: locationData.state }),
    ...(locationData?.city && { city: locationData.city }),
    ...(locationData?.zip && { zip: locationData.zip }),
    breakpoint: windowSize.width >= ds.breakpoints.lg ? 'lg' : windowSize.width >= ds.breakpoints.md ? 'md' : 'sm',
    ...customCookieAttributes,
    ...cmabAttributes
  }
}

// Across brands there is always a style id represented by the first 8 characters of the product id
const getStyleFromProductId = (productId: string) => productId.slice(0, 8)

const getProductIdFromPLPList = (product) => {
  const isStyle = PRODUCT_STYLE_ID_REGEX.test(product.id)
  if (!isStyle) return product.id
  return getDefaultColorOption(product.attributes, product.id).id
}

const OPTIMIZELY_PAYMENT_METHOD_MAP: Record<string, string> = {
  CREDIT_CARD: 'credit_card',
  PAYPAL: 'paypal',
  PAYPAL_EXPRESS: 'paypal',
  KLARNA: 'klarna',
  DW_APPLE_PAY: 'apple_pay',
  APPLE_PAY_EXPRESS: 'apple_pay',
  SHOP_PAY: 'shop_pay',
}

/**
 * Maps a payment-method code list to the single Optimizely `payment_method` property value.
 * Returns the first recognised method (gift cards / loyalty / etc. are skipped).
 * @param paymentMethods - Order payment methods in API shape (`code` required).
 * @returns Optimizely payment method slug, or `undefined` when none match.
 * @category Utils
 */
export const getOptimizelyPaymentMethod = (
  paymentMethods: { code?: string }[] = []
): string | undefined => {
  for (const { code } of paymentMethods) {
    if (!code) continue
    const mapped = OPTIMIZELY_PAYMENT_METHOD_MAP[code.toUpperCase()]
    if (mapped) return mapped
  }
}

/**
 * Returns the shipping-method code for the Optimizely `shipping_method` property.
 * Passes the API code through unchanged (no remapping).
 * @param code - Shipping method code from the order shipment.
 * @returns The code as received, or `undefined` when missing/blank.
 * @category Utils
 */
export const getOptimizelyShippingMethod = (
  code?: string
): string | undefined => {
  const normalized = code?.trim()
  if (!normalized) return
  return normalized
}

/**
 * Projects the raw event payload for a given analytics event onto the schema downstream
 * destinations (e.g. Optimizely) expect. Each `case` corresponds to a known event key
 * (`product_view`, `quick_shop`, `order_summary`, etc.) and returns only the fields that event
 * cares about, derived (style ID, joined SKU lists, …) where required.
 * @param eventKey - The analytics event key driving the projection.
 * @param properties - Raw event payload coming from the caller.
 * @returns The projected payload for `eventKey`, or `properties` unchanged when the key is unknown.
 * @category Utils
 */
export const mapProperties = (eventKey: string, properties) => {
  switch (eventKey) {
    case 'quick_shop': {
      const { appSource, product } = properties
      return {
        appSource,
        product,
        style: getStyleFromProductId(product)
      }
    }
    case 'order_summary': {
      const { total, items } = properties
      return {
        total,
        products: items.map(({ masterId }) => masterId).join(' - '),
        styles: items.map(({ masterId }) => getStyleFromProductId(masterId)).join(' - '),
        SKUS: items.map(({ productId }) => productId).join(' - ')
      }
    }
    case 'checkout': {
      const { payment_method, user_type } = properties
      return {
        ...(payment_method && { payment_method }),
        user_type
      }
    }
    case 'order_confirmation': {
      const { total, items, transactionId, shipping_method } = properties
      return {
        total,
        products: items.map(({ masterId }) => masterId).join(' - '),
        styles: items.map(({ masterId }) => getStyleFromProductId(masterId)).join(' - '),
        SKUS: items.map(({ productId }) => productId).join(' - '),
        transactionId,
        ...(shipping_method && { shipping_method })
      }
    }
    case 'bounce_back_to_cart': {
      return {}
    }

    case 'product_add': {
      const { total, items, itemsToAdd } = properties
      return {
        total,
        product: extractCartProducts(items, itemsToAdd, 'masterId').join(' | '),
        style: extractCartProducts(items, itemsToAdd, 'masterId').map((id) => getStyleFromProductId(id)).join(' | '),
        SKU: extractCartProducts(items, itemsToAdd, 'productId').join(' | ')
      }
    }

    case 'product_remove': {
      const { total, product } = properties
      return {
        total,
        product: product?.masterId,
        style: getStyleFromProductId(product?.masterId),
        SKU: product?.productId
      }
    }

    case 'category_view': {
      const { products, categoryName, categoryID, categoryBreadcrumbs, productsPerPage } = properties
      return {
        products: products.slice(0, productsPerPage).map((product) => getProductIdFromPLPList(product)).join(' - '),
        styles: products.slice(0, productsPerPage).map((product) => getStyleFromProductId(product.id)).join(' - '),
        categoryName,
        categoryID,
        categoryBreadcrumbs
      }
    }

    case 'product_view': {
      const { appSource, product } = properties
      return {
        appSource,
        product: product.id,
        productName: product.name,
        style: product.id.slice(0, 8)
      }
    }

    case 'upsell_product_click': {
      return { position: properties.position }
    }

    default:
      return properties
  }
}
